/**
 * api.js — Central API utility for Project 217
 *
 * All API calls go through apiRequest() or apiFetch().
 * Auth token is attached automatically with the correct Bearer prefix.
 * Never import API_BASE or construct auth headers directly in components.
 *
 * Usage:
 *   import { apiRequest, apiFetch } from "../utils/api";
 *
 *   // Simple GET — returns parsed JSON
 *   const data = await apiRequest("/fulfillment/records");
 *
 *   // With options (POST, PATCH, etc.)
 *   const data = await apiRequest("/fulfillment/records/123", {
 *     method: "PATCH",
 *     body: JSON.stringify({ Status: "Sent" }),
 *   });
 *
 *   // Raw fetch — returns Response object (use when you need status code directly)
 *   const res = await apiFetch("/fulfillment/upload-url");
 *   const { uploadUrl } = await res.json();
 */

import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE = process.env.REACT_APP_API_BASE;

/**
 * Returns Authorization header with Bearer-prefixed Cognito ID token.
 * Used internally by apiRequest and apiFetch.
 */
async function getAuthHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Makes an authenticated API request and returns parsed JSON.
 * Throws an Error if the response is not ok (4xx, 5xx).
 *
 * @param {string} path - API path, e.g. "/fulfillment/records"
 * @param {RequestInit} options - fetch options (method, body, etc.)
 * @returns {Promise<any>} Parsed JSON response body
 */
export async function apiRequest(path, options = {}) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...authHeaders,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.data   = data;
    throw err;
  }
  return data;
}

/**
 * Makes an authenticated API request and returns the raw Response object.
 * Use when you need access to the response status or headers directly,
 * or when the response may not be JSON (e.g. presigned URL flows).
 *
 * @param {string} path - API path, e.g. "/fulfillment/upload-url"
 * @param {RequestInit} options - fetch options (method, body, etc.)
 * @returns {Promise<Response>} Raw fetch Response
 */
export async function apiFetch(path, options = {}) {
  const authHeaders = await getAuthHeaders();
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...authHeaders,
      ...(options.headers || {}),
    },
  });
}
